import SwapVertIcon from "@mui/icons-material/SwapVert";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../../../../context/AlertContext";
import { useVideo } from "../../../../../context/juno/VideoContext";
import { useVideoMetadata } from "../../../../../hooks/useVideoMetadata";

import {
  isDurationSliderDisabledForEngine,
  isLoopSwitchDisabled,
  shouldForceDuration,
} from "../../../../../utils/validation/videoValidation";
import { ColumnBox, RowCenteredBox } from "../../../../../utils/styledBox";
import LumaConceptsControl from "../LumaConceptsControl";
import AspectRatioIcons from "../AspectRatioIcons";
import ImageUpload from "../ImageUpload";
import VideoUpload from "../VideoUpload";
import ModelVersionSelectBox from "../ModelVersionSelectBox";

const ParamsLuma = () => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const { extractMetadataFromBase64 } = useVideoMetadata();
  const {
    menu,
    updateParams,
    image1,
    setImage1,
    duration,
    modelVersion,
    setLumaLoop,
    lumaLoop,
    image2,
    setImage2,
    video1,
    setVideo1,
    setVideo1Metadata,
  } = useVideo();

  // i2vとt2v用のアスペクト比
  const aspectRatio = ["16:9", "4:3", "9:16", "3:4"];

  // v2v（Reframe）用のアスペクト比（より多くの選択肢）
  const reframeAspectRatio = ["1:1", "16:9", "9:16", "3:4", "4:3", "21:9", "9:21"];

  const durationMarks = [
    {
      value: 5,
      label: "5s",
    },
    {
      value: 9,
      label: "9s",
    },
  ];

  // モデルに応じて動画の長さを強制
  useEffect(() => {
    const forceDuration = shouldForceDuration("LUMA", modelVersion.LUMA);
    if (forceDuration.shouldForce && duration.LUMA !== forceDuration.forcedValue) {
      updateParams("LUMA", "duration", forceDuration.forcedValue);
    }
  }, [modelVersion.LUMA, duration.LUMA, updateParams]);

  // RAY_2モデルで2枚の画像がセットされている場合、Loopを無効化
  const isLoopDisabled = isLoopSwitchDisabled(modelVersion.LUMA, menu, image1, image2);

  useEffect(() => {
    if (isLoopDisabled) {
      setLumaLoop(false);
    }
  }, [isLoopDisabled, setLumaLoop]);

  const replaceImage = () => {
    const tmp = image1;
    setImage1(image2);
    setImage2(tmp);
  };

  // Reframeモデルかどうかを判定
  const isReframeModel = modelVersion.LUMA === "RAY_2_REFRAME" || modelVersion.LUMA === "RAY_2_FLASH_REFRAME";

  // Reframeモデル用のメタデータ取得とバリデーション
  useEffect(() => {
    if (video1 && isReframeModel) {
      const processVideoMetadata = async () => {
        try {
          const metadata = await extractMetadataFromBase64(video1);
          setVideo1Metadata(metadata);

          // Reframeモデルのバリデーション
          try {
            // 動画の長さをチェック
            const maxDuration = modelVersion.LUMA === "RAY_2_REFRAME" ? 10 : 30;
            if (metadata.duration > maxDuration) {
              setVideo1("");
              setAlert("error", t("juno.video.error.max.reframeDuration", { duration: maxDuration }));
              return;
            }

            // ファイルサイズは100MBまで（バリデーションファイルで処理済み）
          } catch (error) {
            console.error(error);
            setVideo1("");
            const maxDuration = modelVersion.LUMA === "RAY_2_REFRAME" ? 10 : 30;
            setAlert("error", t("juno.video.error.max.reframeDuration", { duration: maxDuration }));
            return;
          }
        } catch (error) {
          console.error(error);
          setVideo1("");
          setAlert("error", t("juno.video.error.generate"));
          return;
        }
      };

      processVideoMetadata();
    } else if (!video1) {
      setVideo1Metadata(null);
    }
  }, [video1, isReframeModel, modelVersion.LUMA, setVideo1Metadata, setVideo1, setAlert, t, extractMetadataFromBase64]);

  return (
    <ColumnBox sx={{ gap: 4 }}>
      <ColumnBox>
        {/* Model */}
        <Typography variant="body2" mb={1}>
          {t("juno.video.ai.model")}
        </Typography>
        <ModelVersionSelectBox onChange={(value) => updateParams("LUMA", "modelVersion", value)} />
        {!isReframeModel && (
          <Typography variant="caption" mt={1} color={"warning"}>
            {t("juno.video.extend.extendable")}
          </Typography>
        )}
      </ColumnBox>

      {/* Camera Concepts - Reframeモデルでは非表示 */}
      {modelVersion.LUMA.includes("RAY_2") && !isReframeModel && (
        <ColumnBox>
          {/* Camera Concepts */}
          <Typography variant="body2" mb={1}>
            {t("juno.video.concepts")}
          </Typography>
          <LumaConceptsControl />
        </ColumnBox>
      )}

      {/* Duration - Reframeモデルでは非表示 */}
      {!isReframeModel && (
        <ColumnBox sx={{}}>
          {/* Duration */}
          <Typography variant="body2" mb={1}>
            {t("juno.video.params.duration")}
          </Typography>
          <Slider
            aria-label="duration"
            marks={durationMarks}
            min={5}
            max={9}
            step={4}
            value={duration.LUMA}
            onChange={(_, value) => updateParams("LUMA", "duration", value)}
            disabled={isDurationSliderDisabledForEngine("LUMA", modelVersion.LUMA, menu)}
          />
        </ColumnBox>
      )}

      {menu === "i2v" && (
        <ColumnBox sx={{ justifyContent: "center" }}>
          <Typography variant="body2" mb={1}>
            {t("juno.video.params.upload")} - FIRST
          </Typography>
          <ImageUpload image={image1} setImage={setImage1} />
          <RowCenteredBox sx={{ my: 2 }}>
            <IconButton onClick={() => replaceImage()}>
              <SwapVertIcon />
            </IconButton>
          </RowCenteredBox>
          <Typography variant="body2" mb={1}>
            {t("juno.video.params.upload")} - LAST
          </Typography>
          <ImageUpload image={image2} setImage={setImage2} />
        </ColumnBox>
      )}

      {menu === "v2v" && (
        <ColumnBox sx={{ justifyContent: "center" }}>
          <Typography variant="body2" mb={1}>
            {t("juno.video.params.uploadVideo")}
          </Typography>
          <VideoUpload video={video1} setVideo={setVideo1} />
          <Typography variant="caption" mt={1} color={"warning"}>
            {t("juno.video.reframe.maxDuration", {
              duration: modelVersion.LUMA === "RAY_2_REFRAME" ? "10" : "30",
              maxSize: "100MB",
            })}
          </Typography>
        </ColumnBox>
      )}

      <ColumnBox>
        {/* aspect */}
        <AspectRatioIcons aspectRatioList={menu === "v2v" ? reframeAspectRatio : aspectRatio} />
      </ColumnBox>

      {/* Loop - Reframeモデルでは非表示 */}
      {!isReframeModel && (
        <ColumnBox>
          {/* loop */}
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={lumaLoop} onChange={() => setLumaLoop(!lumaLoop)} disabled={isLoopDisabled} />}
              label={t("juno.video.params.loop")}
            />
          </FormGroup>
        </ColumnBox>
      )}
    </ColumnBox>
  );
};

export default ParamsLuma;
